import { useState } from 'react';
import { TemplateMeasurementConfig, MeasurementReading } from '@easydone/shared';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Timestamp as FirestoreTimestamp } from 'firebase/firestore';

interface MeasurementRecorderProps {
  definitions: TemplateMeasurementConfig[];
  entityType: 'floc' | 'asset';
  entityId: string;
  entityName: string;
  onRecord: (measurements: Omit<MeasurementReading, 'id' | 'timestamp' | 'createdAt'>[]) => Promise<void>;
  initialValues?: Map<string, any>;
  userId: string;
  userName: string;
  inspectionId?: string;
}

export function MeasurementRecorder({
  definitions,
  entityType,
  entityId,
  entityName,
  onRecord,
  initialValues,
  userId,
  userName,
  inspectionId,
}: MeasurementRecorderProps) {
  const [values, setValues] = useState<Map<string, any>>(
    initialValues || new Map()
  );
  const [notes, setNotes] = useState<Map<string, string>>(new Map());
  const [recording, setRecording] = useState(false);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [selectedDefinitions, setSelectedDefinitions] = useState<Set<string>>(new Set());
  const [showSelection, setShowSelection] = useState(true);

  const filteredDefinitions = definitions.filter(
    (def) =>
      def.context === 'both' ||
      def.context === entityType
  );

  const toggleDefinitionSelection = (defId: string) => {
    const newSelected = new Set(selectedDefinitions);
    if (newSelected.has(defId)) {
      newSelected.delete(defId);
    } else {
      newSelected.add(defId);
    }
    setSelectedDefinitions(newSelected);
  };

  const toggleCategorySelection = (category: string) => {
    const categoryDefs = filteredDefinitions.filter(d => (d.category || 'Uncategorized') === category);
    const allSelected = categoryDefs.every(d => selectedDefinitions.has(d.id));

    const newSelected = new Set(selectedDefinitions);
    categoryDefs.forEach(d => {
      if (allSelected) {
        newSelected.delete(d.id);
      } else {
        newSelected.add(d.id);
      }
    });
    setSelectedDefinitions(newSelected);
  };

  const proceedToRecord = () => {
    if (selectedDefinitions.size === 0) {
      alert('Please select at least one measurement to record');
      return;
    }
    setShowSelection(false);
  };

  // Group definitions by category
  const groupedDefinitions = filteredDefinitions.reduce((groups, def) => {
    const category = def.category || 'Uncategorized';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(def);
    return groups;
  }, {} as Record<string, typeof filteredDefinitions>);

  const setValue = (defId: string, value: any) => {
    const newValues = new Map(values);
    newValues.set(defId, value);
    setValues(newValues);

    // Clear error when value is set
    if (errors.has(defId)) {
      const newErrors = new Map(errors);
      newErrors.delete(defId);
      setErrors(newErrors);
    }
  };

  const setNote = (defId: string, note: string) => {
    const newNotes = new Map(notes);
    newNotes.set(defId, note);
    setNotes(newNotes);
  };

  const validateMeasurement = (
    def: TemplateMeasurementConfig,
    value: any
  ): { valid: boolean; status: 'normal' | 'warning' | 'critical'; error?: string } => {
    // Required check
    if (def.required && (value === undefined || value === null || value === '')) {
      return { valid: false, status: 'normal', error: 'This measurement is required' };
    }

    // Skip further validation if no value
    if (value === undefined || value === null || value === '') {
      return { valid: true, status: 'normal' };
    }

    // Type validation
    if (def.dataType === 'number') {
      const numValue = typeof value === 'number' ? value : parseFloat(value);

      if (isNaN(numValue)) {
        return { valid: false, status: 'normal', error: 'Must be a valid number' };
      }

      // Range validation
      if (def.minValue !== undefined && numValue < def.minValue) {
        return {
          valid: false,
          status: 'normal',
          error: `Must be >= ${def.minValue}`,
        };
      }

      if (def.maxValue !== undefined && numValue > def.maxValue) {
        return {
          valid: false,
          status: 'normal',
          error: `Must be <= ${def.maxValue}`,
        };
      }

      // Threshold checks
      if (def.criticalThreshold) {
        if (
          (def.criticalThreshold.min !== undefined &&
            numValue < def.criticalThreshold.min) ||
          (def.criticalThreshold.max !== undefined &&
            numValue > def.criticalThreshold.max)
        ) {
          return { valid: true, status: 'critical' };
        }
      }

      if (def.warningThreshold) {
        if (
          (def.warningThreshold.min !== undefined &&
            numValue < def.warningThreshold.min) ||
          (def.warningThreshold.max !== undefined &&
            numValue > def.warningThreshold.max)
        ) {
          return { valid: true, status: 'warning' };
        }
      }
    }

    return { valid: true, status: 'normal' };
  };

  const handleSubmit = async () => {
    const newErrors = new Map<string, string>();

    // Validate only selected measurements
    const selectedDefs = filteredDefinitions.filter(def => selectedDefinitions.has(def.id));

    selectedDefs.forEach((def) => {
      const value = values.get(def.id);
      const validation = validateMeasurement(def, value);

      if (!validation.valid) {
        newErrors.set(def.id, validation.error || 'Invalid value');
      }
    });

    if (newErrors.size > 0) {
      setErrors(newErrors);
      return;
    }

    setRecording(true);

    try {
      const measurements: Omit<
        MeasurementReading,
        'id' | 'timestamp' | 'createdAt'
      >[] = [];

      selectedDefs.forEach((def) => {
        const value = values.get(def.id);

        // Skip if no value and not required
        if (
          (value === undefined || value === null || value === '') &&
          !def.required
        ) {
          return;
        }

        const validation = validateMeasurement(def, value);

        const measurement: Omit<
          MeasurementReading,
          'id' | 'timestamp' | 'createdAt'
        > = {
          orgId: '', // Will be set by service
          measurementDefId: def.id,
          measurementName: def.name,
          measurementCategory: def.category,
          value: def.dataType === 'number' ? parseFloat(value) : value,
          numericValue: def.dataType === 'number' ? parseFloat(value) : undefined,
          unit: def.unit,
          readingDate: FirestoreTimestamp.now(),
          source: inspectionId ? 'inspection' : 'manual',
          ...(inspectionId && { inspectionId }), // Only include if defined
          recordedBy: userId,
          recordedByName: userName,
          quality: 'good',
          verified: false,
          status: validation.status,
          ...(notes.get(def.id) && { notes: notes.get(def.id) }), // Only include if defined
        };

        // Add entity info
        if (entityType === 'floc') {
          measurement.flocId = entityId;
          measurement.flocName = entityName;
        } else {
          measurement.assetId = entityId;
          measurement.assetName = entityName;
        }

        measurements.push(measurement);
      });

      await onRecord(measurements);

      // Clear form
      setValues(new Map());
      setNotes(new Map());
    } catch (error) {
      console.error('Failed to record measurements:', error);
      alert('Failed to record measurements. Please try again.');
    } finally {
      setRecording(false);
    }
  };

  const renderInput = (def: TemplateMeasurementConfig) => {
    const value = values.get(def.id);
    const note = notes.get(def.id) || '';
    const error = errors.get(def.id);
    const validation = validateMeasurement(def, value);

    const getStatusColor = () => {
      if (error) return 'border-red-300';
      if (validation.status === 'critical') return 'border-red-500';
      if (validation.status === 'warning') return 'border-yellow-500';
      return 'border-gray-300';
    };

    const getStatusIcon = () => {
      if (error)
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      if (validation.status === 'critical')
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      if (validation.status === 'warning')
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />;
      if (value !== undefined && value !== null && value !== '')
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      return null;
    };

    return (
      <div key={def.id} className="card">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-900">
              {def.name}
              {def.required && <span className="text-red-600 ml-1">*</span>}
            </label>
            {def.description && (
              <p className="text-xs text-gray-600 mt-1">{def.description}</p>
            )}
          </div>
          {getStatusIcon()}
        </div>

        <div className="space-y-2">
          {def.dataType === 'number' && (
            <div className="flex gap-2">
              <input
                type="number"
                value={value ?? ''}
                onChange={(e) => setValue(def.id, e.target.value)}
                className={`input flex-1 ${getStatusColor()}`}
                placeholder={`Enter ${def.name.toLowerCase()}`}
                step={def.precision !== undefined ? Math.pow(10, -def.precision) : 'any'}
              />
              {def.unit && (
                <div className="flex items-center px-3 bg-gray-100 rounded text-sm text-gray-700">
                  {def.unit}
                </div>
              )}
            </div>
          )}

          {def.dataType === 'text' && (
            <input
              type="text"
              value={value ?? ''}
              onChange={(e) => setValue(def.id, e.target.value)}
              className={`input w-full ${getStatusColor()}`}
              placeholder={`Enter ${def.name.toLowerCase()}`}
            />
          )}

          {def.dataType === 'boolean' && (
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={def.id}
                  checked={value === true}
                  onChange={() => setValue(def.id, true)}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={def.id}
                  checked={value === false}
                  onChange={() => setValue(def.id, false)}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-700">No</span>
              </label>
            </div>
          )}

          {def.dataType === 'date' && (
            <input
              type="date"
              value={value ?? ''}
              onChange={(e) => setValue(def.id, e.target.value)}
              className={`input w-full ${getStatusColor()}`}
            />
          )}

          {def.dataType === 'select' && (
            <select
              value={value ?? ''}
              onChange={(e) => setValue(def.id, e.target.value)}
              className={`input w-full ${getStatusColor()}`}
            >
              <option value="">Select an option</option>
              {(def.options || []).map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}

          {/* Threshold indicators */}
          {def.dataType === 'number' && value !== undefined && value !== '' && (
            <div className="flex items-center gap-2 text-xs">
              {validation.status === 'critical' && (
                <span className="text-red-600 font-medium">
                  ⚠ Critical threshold exceeded
                </span>
              )}
              {validation.status === 'warning' && (
                <span className="text-yellow-600 font-medium">
                  ⚠ Warning threshold exceeded
                </span>
              )}
              {validation.status === 'normal' && value && (
                <span className="text-green-600">✓ Within normal range</span>
              )}
            </div>
          )}

          {/* Notes */}
          <textarea
            value={note}
            onChange={(e) => setNote(def.id, e.target.value)}
            className="input w-full text-sm"
            rows={2}
            placeholder="Add notes (optional)"
          />

          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}
        </div>
      </div>
    );
  };

  if (filteredDefinitions.length === 0) {
    return (
      <div className="card text-center py-8">
        <p className="text-gray-600">
          No measurements defined for {entityType === 'floc' ? 'FLOCs' : 'assets'}
        </p>
      </div>
    );
  }

  // Selection View
  if (showSelection) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Select Measurements to Record
          </h3>
          <p className="text-sm text-gray-600">
            {entityType === 'floc' ? 'FLOC' : 'Asset'}: {entityName} • {selectedDefinitions.size} selected
          </p>
        </div>

        <div className="space-y-3">
          {Object.entries(groupedDefinitions).map(([category, defs]) => {
            const allSelected = defs.every(d => selectedDefinitions.has(d.id));

            return (
              <div key={category} className="card">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">{category}</h4>
                  <button
                    onClick={() => toggleCategorySelection(category)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="space-y-2">
                  {defs.map(def => (
                    <label
                      key={def.id}
                      className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDefinitions.has(def.id)}
                        onChange={() => toggleDefinitionSelection(def.id)}
                        className="mt-1 h-4 w-4 text-blue-600 rounded"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {def.name}
                          </span>
                          {def.unit && (
                            <span className="text-xs text-gray-500">({def.unit})</span>
                          )}
                          {def.required && (
                            <span className="text-xs text-red-600">Required</span>
                          )}
                        </div>
                        {def.description && (
                          <p className="text-xs text-gray-600 mt-0.5">{def.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={proceedToRecord}
            className="btn btn-primary"
            disabled={selectedDefinitions.size === 0}
          >
            Continue ({selectedDefinitions.size} selected)
          </button>
        </div>
      </div>
    );
  }

  // Input View - only show selected measurements
  const selectedDefs = filteredDefinitions.filter(def => selectedDefinitions.has(def.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Record Measurements
          </h3>
          <p className="text-sm text-gray-600">
            {entityType === 'floc' ? 'FLOC' : 'Asset'}: {entityName} • {selectedDefs.length} measurements
          </p>
        </div>
        <button
          onClick={() => setShowSelection(true)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Change Selection
        </button>
      </div>

      <div className="space-y-3">
        {selectedDefs.map((def) => renderInput(def))}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={() => {
            setValues(new Map());
            setNotes(new Map());
            setErrors(new Map());
          }}
          className="btn btn-secondary"
          disabled={recording}
        >
          Clear All
        </button>
        <button
          onClick={handleSubmit}
          className="btn btn-primary"
          disabled={recording}
        >
          {recording ? 'Recording...' : 'Record Measurements'}
        </button>
      </div>
    </div>
  );
}
